/**
 * UTC time buckets for the dashboard's uploads chart.
 *
 * The server returns per-UTC-day counts (`daily_uploads` / `daily_storage`,
 * keyed "YYYY-MM-DD"); the chart rolls them up into daily, weekly
 * (Monday-based) or monthly bars depending on how long the selected window
 * is, so the bars stay readable. Bucket keys are ISO strings that sort as
 * strings: "YYYY-MM-DD" for day and week (the week's Monday), "YYYY-MM" for
 * month.
 */
export const MS_PER_DAY = 86400000;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_KEY = /^\d{4}-\d{2}$/;

const pad = (n) => String(n).padStart(2, "0");
const dayKeyOf = (ms) => new Date(ms).toISOString().slice(0, 10);
const isoSeconds = (ms) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");

/**
 * Bucket granularity for a window: daily up to about three months, weekly up
 * to about two years, monthly beyond.
 */
export const bucketModeForSpan = (fromMs, toMs) => {
  const days = (toMs - fromMs) / MS_PER_DAY;
  if (days <= 92) return "day";
  if (days <= 731) return "week";
  return "month";
};

/** Key of the bucket containing the instant `ms` (epoch milliseconds). */
export const bucketKey = (ms, mode) => {
  const d = new Date(ms);
  if (mode === "month") return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
  if (mode === "week") {
    const monday = Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate() - ((d.getUTCDay() + 6) % 7)
    );
    return dayKeyOf(monday);
  }
  return dayKeyOf(ms);
};

/** Start instant (epoch ms) of bucket `key`, or NaN for a malformed key. */
const bucketStartMs = (key, mode) => {
  if (mode === "month") {
    if (!MONTH_KEY.test(key || "")) return NaN;
    const [year, month] = key.split("-").map(Number);
    return Date.UTC(year, month - 1, 1);
  }
  if (!DAY_KEY.test(key || "")) return NaN;
  return Date.parse(`${key}T00:00:00Z`);
};

/** Key of the bucket after `key`. */
const nextKey = (key, mode) => {
  const start = bucketStartMs(key, mode);
  if (mode === "month") {
    const d = new Date(start);
    return bucketKey(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1), mode);
  }
  return bucketKey(start + (mode === "week" ? 7 : 1) * MS_PER_DAY, mode);
};

/**
 * Every bucket key from the one containing `fromMs` to the one containing
 * `toMs`, in order; empty when the range is inverted.
 */
export const bucketKeys = (fromMs, toMs, mode) => {
  const keys = [];
  const last = bucketKey(toMs, mode);
  for (let key = bucketKey(fromMs, mode); key <= last; key = nextKey(key, mode)) {
    keys.push(key);
  }
  return keys;
};

/**
 * Inclusive bounds of bucket `key` as ISO-8601 UTC instants at second
 * precision (the list page's time-window format); undefined for a key of
 * the wrong shape.
 */
export const bucketBounds = (key, mode) => {
  const start = bucketStartMs(key, mode);
  if (Number.isNaN(start)) return undefined;
  const nextStart = bucketStartMs(nextKey(key, mode), mode);
  return { from: isoSeconds(start), to: isoSeconds(nextStart - 1000) };
};

/**
 * Sum the per-day values of `daily` ("YYYY-MM-DD" → number) into the given
 * buckets, in their order; days outside the buckets are ignored.
 */
export const rollUp = (daily, keys, mode) => {
  const sums = Object.fromEntries(keys.map((key) => [key, 0]));
  Object.entries(daily || {}).forEach(([day, value]) => {
    if (!DAY_KEY.test(day)) return;
    const key = bucketKey(Date.parse(`${day}T00:00:00Z`), mode);
    if (key in sums) sums[key] += value;
  });
  return keys.map((key) => sums[key]);
};

/**
 * Range and buckets of the uploads chart for a dashboard window.
 *
 * The chart always runs on upload time: an upload window is its range; a
 * recording window only fixes the lower end, since a file is uploaded after
 * it is recorded, and the chart runs up to now; with no window it covers
 * the trailing `defaultDays` days.
 */
export const uploadsChartRange = ({ field, from, to }, nowMs, defaultDays = 30) => {
  const end = field === "uploaded" && to ? Date.parse(to) : nowMs;
  const start = from ? Date.parse(from) : end - (defaultDays - 1) * MS_PER_DAY;
  const mode = bucketModeForSpan(start, end);
  return { start, end, mode, keys: bucketKeys(start, end, mode) };
};
